/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState } from "react";
import moment from "moment-timezone";
import { getFileDownloadBaseURL } from "config";
import {
  DataTable,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
} from "carbon-components-react";
import APIClient from "../APIClient";

interface Item {
  last_modified?: string;
  key?: string;
}

interface MctRpts {
  contents?: Item[];
}

const tz = moment.tz.guess();
const formatTimeLocal = (t: string): string => moment(t).tz(tz).format("MM/DD/YY h:mma z");
const formatTimePacific = (t: string): string =>
  moment(t).tz("America/Los_Angeles").format("MM/DD/YY h:mma z");
const formatDatePacific = (t: string): string =>
  moment(t).tz("America/Los_Angeles").format("YYYY-MM-DD");

const MCT: React.FC<any> = () => {
  const [mctReports, setMCTReports] = useState<MctRpts>({});

  useEffect(() => {
    APIClient.get("/uw/mct_reports").then((resp: any) => {
      setMCTReports(resp);
    });
  }, []);

  const MCT_FILENAME_PATTERN = /(^mct_reports\/mct_report_(?<timestamp>.*)\.csv$)/;

  const mctFilenameToDisplay = (filename: string): string => {
    const found = filename.match(MCT_FILENAME_PATTERN);
    if (found && found.groups && found.groups.timestamp) {
      return formatDatePacific(found.groups.timestamp);
    }
    return filename;
  };

  const reports = mctReports.contents
    ? mctReports.contents
        .filter(item => moment(item.last_modified).isAfter("2021-05-01"))
        .map(item => {
          return {
            download_link: item.key,
            id: item.key,
            created_at: item.last_modified,
            created_at_pacific: item.last_modified,
          };
        })
    : [];

  return (
    <>
      <DataTable
        rows={reports}
        headers={[
          { header: "Download", key: "download_link" },
          { header: "Created At (Pacific TZ)", key: "created_at_pacific" },
          { header: "Created At (Local TZ)", key: "created_at" },
        ]}
        isSortable
      >
        {({
          rows,
          headers,
          getHeaderProps,
          getRowProps,
          getToolbarProps,
          onInputChange,
          getTableContainerProps,
        }) => (
          <TableContainer title="MCT Reports" {...getTableContainerProps()}>
            <TableToolbar {...getToolbarProps()} aria-label="data table toolbar">
              <TableToolbarContent>
                <TableToolbarSearch onChange={onInputChange} />
              </TableToolbarContent>
            </TableToolbar>
            <Table size="compact">
              <TableHead>
                <TableRow>
                  {headers.map(header => (
                    <TableHeader key={header.key} {...getHeaderProps({ header })}>
                      {header.header}
                    </TableHeader>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(row => (
                  <TableRow key={row.id} {...getRowProps({ row })}>
                    {row.cells.map(cell => {
                      if (cell.info.header === "download_link") {
                        return (
                          <TableCell key={cell.id}>
                            <a
                              href={getFileDownloadBaseURL(
                                cell.value,
                                `mct_report_${mctFilenameToDisplay(cell.value)}`,
                              )}
                            >
                              {mctFilenameToDisplay(cell.value)}
                            </a>
                          </TableCell>
                        );
                      }
                      if (cell.info.header === "created_at") {
                        /* Formatting here so sorting is easy */
                        return <TableCell key={cell.id}>{formatTimeLocal(cell.value)}</TableCell>;
                      }
                      if (cell.info.header === "created_at_pacific") {
                        /* Formatting here so sorting is easy */
                        return <TableCell key={cell.id}>{formatTimePacific(cell.value)}</TableCell>;
                      }
                      return <TableCell key={cell.id}>{cell.value}</TableCell>;
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DataTable>
    </>
  );
};

export default MCT;
